'use client';

import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { Info, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { ocrTemplateQueries } from '../services/ocr-templates.queries';

interface TemplateFieldsInfoProps {
  /** Template whose active extracted fields to preview. */
  templateId: number | null | undefined;
  /** Extra classes for the trigger — e.g. icon colour/size per surface. */
  className?: string;
}

interface FieldItem {
  key: string;
  name: string;
}

/**
 * Info icon that reveals — on hover or click — the fields a given OCR template
 * actively extracts: the form fields it keeps (minus the excluded ones) plus
 * its active custom fields. The template detail is fetched lazily the first
 * time the popover opens and cached by TanStack Query, so listing surfaces that
 * only carry `{ id, name }` pay nothing until the user actually asks.
 */
export function TemplateFieldsInfo({
  templateId,
  className
}: TemplateFieldsInfoProps) {
  const t = useTranslations('ocrTemplates');
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const id = templateId ?? 0;

  const { data, isPending, isError } = useQuery({
    ...ocrTemplateQueries.detail(id),
    enabled: opened && id > 0
  });

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  if (id <= 0) return null;

  const template = data?.data;
  // Dynamic-form fields no longer take part in OCR — the preview lists only the
  // template's active custom fields. Re-enable alongside the config page's form
  // section + the backend OcrExtractionPipeline::buildTemplateFields() block.
  // const excluded = new Set(template?.excluded_form_field_ids ?? []);
  // const formFields: FieldItem[] = (template?.dynamic_form?.fields ?? [])
  //   .filter((f) => !excluded.has(f.id))
  //   .slice()
  //   .sort((a, b) => a.sort_order - b.sort_order)
  //   .map((f) => ({ key: `form-${f.id}`, name: f.label }));
  const customFields: FieldItem[] = (template?.custom_fields ?? [])
    .filter((f) => f.is_active)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((f) => ({
      key: `custom-${f.id}`,
      // OMA-594: English UI prefers the English label when the template
      // carries one, otherwise the Arabic name stands in.
      name:
        (locale === 'en' ? f.display_name_en?.trim() : null) || f.display_name
    }));
  const totalFields = customFields.length;

  function show() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpened(true);
    setOpen(true);
  }

  function hide() {
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  }

  function toggle() {
    if (open) {
      setOpen(false);
    } else {
      show();
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setOpened(true);
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <span
          role='button'
          tabIndex={0}
          aria-label={t('extractedFieldsAria')}
          className={cn(
            'text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex shrink-0 cursor-pointer items-center rounded-full align-middle outline-none focus-visible:ring-2',
            className
          )}
          onClick={(e) => {
            // Don't bubble to a parent selection control (label / chip / row).
            e.preventDefault();
            e.stopPropagation();
            toggle();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              toggle();
            }
          }}
          onPointerEnter={(e) => {
            if (e.pointerType === 'mouse') show();
          }}
          onPointerLeave={(e) => {
            if (e.pointerType === 'mouse') hide();
          }}
        >
          <Info className='h-3.5 w-3.5' />
        </span>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-64 p-0'
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={show}
        onPointerLeave={hide}
        // The content portals outside any open Dialog, whose scroll lock
        // cancels wheel/touch events at `document` for everything outside the
        // dialog — so the field list could not be scrolled from inside the
        // Start OCR dialog. Stopping propagation here keeps them local.
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <div className='border-b px-3 py-2'>
          <p className='text-xs font-semibold'>
            {t('extractedFieldsTitle', { count: totalFields })}
          </p>
        </div>
        <div className='max-h-64 overflow-y-auto p-2'>
          {isPending ? (
            <div className='text-muted-foreground flex items-center justify-center gap-2 py-4 text-xs'>
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
              {t('extractedFieldsLoading')}
            </div>
          ) : isError ? (
            <p className='text-muted-foreground py-3 text-center text-xs'>
              {t('extractedFieldsError')}
            </p>
          ) : totalFields === 0 ? (
            <p className='text-muted-foreground py-3 text-center text-xs'>
              {t('extractedFieldsEmpty')}
            </p>
          ) : (
            <div className='space-y-2'>
              {/* Form-field group disabled — OCR uses custom fields only.
              {formFields.length > 0 ? (
                <FieldGroup label={t('fromFormBadge')} items={formFields} />
              ) : null}
              */}
              {customFields.length > 0 ? (
                <FieldGroup label={t('customBadge')} items={customFields} />
              ) : null}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FieldGroup({ label, items }: { label: string; items: FieldItem[] }) {
  return (
    <div className='space-y-1'>
      <p className='text-muted-foreground px-2 text-[10px] font-medium uppercase'>
        {label}
      </p>
      <ul className='space-y-0.5'>
        {items.map((item) => (
          <li
            key={item.key}
            className='text-foreground truncate rounded px-2 py-1 text-xs'
            title={item.name}
          >
            {item.name}
          </li>
        ))}
      </ul>
    </div>
  );
}
